import React, { useState, useEffect, useRef } from 'react';
import { Brain, Heart, Smile, Frown, Meh, Menu } from 'lucide-react';
import HealthCard from '../ui/HealthCard';

// Types for the Mental Health Bot
interface MentalHealthMessage {
  id: string;
  sender: 'user' | 'bot';
  content: string;
  timestamp: Date;
  exercises?: MentalHealthExercise[];
  resources?: MentalHealthResource[];
}

interface MentalHealthExercise {
  id: string;
  title: string;
  description: string;
  duration: string;
  type: 'breathing' | 'meditation' | 'cbt' | 'mindfulness';
}

interface MentalHealthResource {
  id: string;
  title: string;
  type: 'article' | 'video' | 'audio';
  url: string;
}

interface MoodEntry {
  date: Date;
  mood: 'great' | 'good' | 'neutral' | 'low' | 'poor';
  notes?: string;
  score: number; // 1-10
}

interface AIMentalHealthBotProps {
  messages?: MentalHealthMessage[];
  moodHistory?: MoodEntry[];
  suggestedExercises?: MentalHealthExercise[];
  className?: string;
}

// Mood icon mapping
const moodIcons = {
  great: <Smile className="h-6 w-6 text-emerald-500" />,
  good: <Smile className="h-6 w-6 text-green-500" />,
  neutral: <Meh className="h-6 w-6 text-blue-500" />,
  low: <Meh className="h-6 w-6 text-amber-500" />,
  poor: <Frown className="h-6 w-6 text-red-500" />
};

/**
 * AI Mental Health Bot Component
 * 
 * An interactive mental health support bot that provides:
 * - Conversational mental wellness support
 * - Mood tracking and journaling
 * - Cognitive behavioral therapy exercises
 * - Guided meditation and relaxation techniques
 * - Mental health resources and education
 */
const AIMentalHealthBot: React.FC<AIMentalHealthBotProps> = ({
  messages = [],
  moodHistory = [],
  suggestedExercises = [],
  className = ''
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [currentMood, setCurrentMood] = useState<MoodEntry['mood']>('neutral');
  const [moodNote, setMoodNote] = useState('');
  const [showMoodInput, setShowMoodInput] = useState(false);
  
  // TTS States
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isFetchingAudio, setIsFetchingAudio] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const processedMessageIds = useRef<Set<string>>(new Set());
  
  // Sample messages if none provided
  const defaultMessages: MentalHealthMessage[] = [
    {
      id: '1',
      sender: 'bot',
      content: "Hello! I'm your mental wellness assistant. How are you feeling today?",
      timestamp: new Date(Date.now() - 60000),
      resources: []
    }
  ];
  
  const chatMessages = messages.length > 0 ? messages : defaultMessages;
  
  // Sample exercises if none provided
  const defaultExercises: MentalHealthExercise[] = [
    {
      id: '1',
      title: '5-Minute Breathing Exercise',
      description: 'A simple breathing technique to reduce anxiety and stress.',
      duration: '5 minutes',
      type: 'breathing'
    },
    {
      id: '2',
      title: 'Thought Reframing Practice',
      description: 'Learn to identify and reframe negative thought patterns.',
      duration: '10 minutes',
      type: 'cbt'
    },
    {
      id: '3',
      title: 'Body Scan Meditation',
      description: 'A guided meditation to connect with your body and release tension.',
      duration: '15 minutes',
      type: 'meditation'
    }
  ];
  
  const exercises = suggestedExercises && suggestedExercises.length > 0
    ? suggestedExercises
    : defaultExercises;

  // Function to fetch audio from backend and play it
  const fetchAndPlayAudio = async (text: string, messageId: string) => {
    if (!text.trim()) return;
    if (processedMessageIds.current.has(messageId)) {
      console.log(`Message ${messageId} already processed for TTS, skipping`);
      return;
    }
    
    setTtsError(null);
    setIsFetchingAudio(true);
    setAudioUrl(null); // Clear previous audio

    // IMPORTANT: Replace with your actual Hugging Face API token
    const HUGGING_FACE_API_TOKEN = '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'; 

    try {
      console.log("🎤 DIA TTS: Sending TTS request to Dia...");
      
      // Format according to Dia docs
      const diaFormattedText = `[S1] ${text} [S1]`;
      console.log("🎤 DIA TTS: Formatted text:", diaFormattedText);
      
      const endpoint = 'https://api-inference.huggingface.co/models/nari-labs/Dia-1.6B';
      console.log("🎤 DIA TTS: Using endpoint:", endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${HUGGING_FACE_API_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          inputs: diaFormattedText,
          options: {
            wait_for_model: true
          }
        }), 
      });

      console.log("🎤 DIA TTS: Response status:", response.status);
      
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("🎤 DIA TTS: Error response:", errorBody);
        throw new Error(`TTS API request failed with status ${response.status}: ${errorBody}`);
      }

      const contentType = response.headers.get('content-type');
      console.log("🎤 DIA TTS: Response content type:", contentType);

      let audioBlob;
      
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        console.log("🎤 DIA TTS: JSON response:", result);
        
        if (result && result.audio_url) {
          console.log("🎤 DIA TTS: Found audio_url in response:", result.audio_url);
          const audioResponse = await fetch(result.audio_url);
          if (!audioResponse.ok) {
            throw new Error(`Failed to fetch audio from URL: ${result.audio_url}`);
          }
          audioBlob = await audioResponse.blob();
        } else if (result && typeof result.audio_data === 'string') {
          console.log("🎤 DIA TTS: Found base64 audio_data in response");
          const byteCharacters = atob(result.audio_data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          audioBlob = new Blob([byteArray], { type: 'audio/mpeg' }); 
        } else {
          console.error("🎤 DIA TTS: Unexpected JSON structure:", result);
          throw new Error('Unexpected API response structure for TTS audio.');
        }
      } else if (contentType && contentType.includes('audio/')) {
        console.log("🎤 DIA TTS: Response appears to be direct audio, reading as blob");
        audioBlob = await response.blob();
        console.log("🎤 DIA TTS: Audio blob size:", audioBlob.size);
      } else {
        console.error("🎤 DIA TTS: Unknown content type:", contentType);
        try {
          audioBlob = await response.blob();
          console.log("🎤 DIA TTS: Fallback: got blob of size", audioBlob.size);
        } catch (blobError) {
          console.error("🎤 DIA TTS: Couldn't read response as blob:", blobError);
          throw new Error(`Unknown response content type: ${contentType}`);
        }
      }

      if (audioBlob && audioBlob.size > 0) {
        console.log("🎤 DIA TTS: Creating audio URL from blob");
        const newAudioUrl = URL.createObjectURL(audioBlob);
        processedMessageIds.current.add(messageId);
        setAudioUrl(newAudioUrl);
        console.log("🎤 DIA TTS: Audio URL created successfully");
      } else {
        console.error("🎤 DIA TTS: No valid audio blob obtained");
        throw new Error('Audio blob could not be created from API response or was empty.');
      }

    } catch (error) {
      console.error('🎤 DIA TTS: Error fetching or playing TTS audio:', error);
      setTtsError(error instanceof Error ? error.message : 'Unknown error fetching audio');
    } finally {
      setIsFetchingAudio(false);
    }
  };

  // Handle form submission (message send)
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newMessage.trim() === '') return;
    
    // In a real app, this would send the message to an API and get a response
    console.log('Sending message to mental health bot:', newMessage);
    
    // Temp: Simulate bot response for testing TTS
    const newBotMessageId = `bot-${Date.now()}`;
    const botResponse = {
      id: newBotMessageId,
      sender: 'bot' as const,
      content: `Thank you for sharing. I'm here to help with any mental health concerns you may have.`,
      timestamp: new Date(),
    };
    
    // In a real app, you would add the message to state here
    // For now, we'll call TTS directly for testing
    setTimeout(() => {
      fetchAndPlayAudio(botResponse.content, botResponse.id);
    }, 500);
    
    // Clear input field
    setNewMessage('');
  };

  // Play audio when audioUrl changes
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.play().catch(e => {
        console.error("🎤 DIA TTS: Error playing audio:", e);
        setTtsError(`Error playing audio: ${e.message}`);
      });
    }
  }, [audioUrl]);

  // Process any bot messages on component mount or when messages change
  useEffect(() => {
    const botMessages = chatMessages.filter(m => m.sender === 'bot');
    
    if (botMessages.length > 0) {
      // Process the most recent bot message if we haven't done it yet
      const lastBotMessage = botMessages[botMessages.length - 1];
      if (lastBotMessage && !processedMessageIds.current.has(lastBotMessage.id)) {
        console.log("🎤 DIA TTS: Processing newest bot message:", lastBotMessage.content);
        
        // Ensure Web Speech API is not speaking
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
          console.log("🎤 DIA TTS: Canceling any active Web Speech API synthesis");
          window.speechSynthesis.cancel();
        }
        
        fetchAndPlayAudio(lastBotMessage.content, lastBotMessage.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages]);

  // Handle message input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
  };
  
  // Handle mood selection
  const handleMoodSelect = (mood: MoodEntry['mood']) => {
    setCurrentMood(mood);
  };
  
  // Handle mood submission
  const handleMoodSubmit = () => {
    const moodScore = {
      'great': 10,
      'good': 8,
      'neutral': 6,
      'low': 4,
      'poor': 2
    }[currentMood];
    
    const newMoodEntry: MoodEntry = {
      date: new Date(),
      mood: currentMood,
      notes: moodNote,
      score: moodScore
    };
    
    console.log('Submitting mood:', newMoodEntry);
    
    // Reset mood input
    setMoodNote('');
    setShowMoodInput(false);
    
    // In a real app, this would call an API to store the mood entry
  };
  
  return (
    <HealthCard 
      title="Mental Wellness Assistant" 
      icon={Brain}
      className={`${className} border-t-4 border-t-purple-500`}
    >
      {audioUrl && (
        <audio 
          ref={audioRef} 
          src={audioUrl} 
          onEnded={() => {
            console.log("🎤 DIA TTS: Audio playback completed");
            setAudioUrl(null);
          }} 
          onError={(e) => {
            console.error("🎤 DIA TTS: Audio playback error:", e);
            setTtsError("Error playing audio");
            setAudioUrl(null);
          }}
          style={{ display: 'none' }} // Hidden audio player
        />
      )}
      {isFetchingAudio && (
        <div className="absolute top-4 right-4 p-2 bg-purple-100 text-purple-700 text-xs rounded-md z-10">
          Generating voice with Dia...
        </div>
      )}
      {ttsError && (
        <div className="absolute top-4 right-4 p-2 bg-red-100 text-red-700 text-xs rounded-md z-10">
          {ttsError}
        </div>
      )}
      <div className="flex flex-col space-y-6">
        {/* Conversation Chat - Full Width */}
        <div className="space-y-5 w-full">
          <div className="flex flex-col space-y-6 h-96 overflow-y-auto p-6 bg-purple-50/50 dark:bg-gray-900/30 rounded-lg">
            {chatMessages.map((message, index) => (
              <div 
                key={message.id || index} 
                className={`flex items-start ${message.sender === 'user' ? 'justify-end' : ''}`}
              >
                {message.sender === 'bot' && (
                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                )}
                
                <div 
                  className={`mx-4 p-4 rounded-lg max-w-[85%] ${
                    message.sender === 'bot' 
                      ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-800 dark:text-gray-200' 
                      : 'bg-purple-100 dark:bg-purple-900/80 text-gray-800 dark:text-white'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.content}</p>
                  
                  {message.exercises && message.exercises.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Suggested exercises:</p>
                      <ul className="text-sm space-y-2.5">
                        {message.exercises.map((exercise) => (
                          <li key={exercise.id} className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-purple-500 mr-2.5 flex-shrink-0"></span>
                            <span className="text-gray-800 dark:text-gray-200">{exercise.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {message.resources && message.resources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Resources:</p>
                      <ul className="text-sm space-y-2.5">
                        {message.resources.map((resource) => (
                          <li key={resource.id}>
                            <a href={resource.url} className="text-purple-600 dark:text-purple-400 hover:underline flex items-center">
                              <span className="w-2 h-2 rounded-full bg-purple-500 mr-2.5 flex-shrink-0"></span>
                              <span>{resource.title}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                {message.sender === 'user' && (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">You</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <form onSubmit={handleSendMessage} className="flex space-x-3">
            <input
              type="text" 
              placeholder="Share how you're feeling or ask for mental health support..." 
              value={newMessage}
              onChange={handleInputChange}
              className="flex-1 p-4 border rounded-lg bg-white dark:bg-gray-800 dark:text-white border-gray-300 dark:border-gray-700 focus:border-purple-500 dark:focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-base"
            />
            <button 
              type="submit"
              className="px-6 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Send
            </button>
          </form>
        </div>
        
        {/* Support Tools - Now Underneath in Two Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          {/* Mood Tracking */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Heart className="h-4 w-4 text-purple-500 mr-2" />
              Mood Tracker
            </h4>
            
            {!showMoodInput ? (
              <button 
                onClick={() => setShowMoodInput(true)}
                className="w-full p-3 mt-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-900/30 rounded-lg flex items-center justify-center hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
              >
                <span className="font-medium">How are you feeling today?</span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <button 
                    onClick={() => handleMoodSelect('poor')}
                    className={`p-2.5 rounded-lg flex flex-col items-center ${currentMood === 'poor' ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    <Frown className="h-6 w-6 text-red-500" />
                    <span className="text-xs mt-1.5">Poor</span>
                  </button>
                  <button 
                    onClick={() => handleMoodSelect('low')}
                    className={`p-2.5 rounded-lg flex flex-col items-center ${currentMood === 'low' ? 'bg-amber-100 dark:bg-amber-900/30 ring-2 ring-amber-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    <Meh className="h-6 w-6 text-amber-500" />
                    <span className="text-xs mt-1.5">Low</span>
                  </button>
                  <button 
                    onClick={() => handleMoodSelect('neutral')}
                    className={`p-2.5 rounded-lg flex flex-col items-center ${currentMood === 'neutral' ? 'bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    <Meh className="h-6 w-6 text-blue-500" />
                    <span className="text-xs mt-1.5">Neutral</span>
                  </button>
                  <button 
                    onClick={() => handleMoodSelect('good')}
                    className={`p-2.5 rounded-lg flex flex-col items-center ${currentMood === 'good' ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    <Smile className="h-6 w-6 text-green-500" />
                    <span className="text-xs mt-1.5">Good</span>
                  </button>
                  <button 
                    onClick={() => handleMoodSelect('great')}
                    className={`p-2.5 rounded-lg flex flex-col items-center ${currentMood === 'great' ? 'bg-emerald-100 dark:bg-emerald-900/30 ring-2 ring-emerald-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    <Smile className="h-6 w-6 text-emerald-500" />
                    <span className="text-xs mt-1.5">Great</span>
                  </button>
                </div>
                
                <textarea
                  value={moodNote}
                  onChange={(e) => setMoodNote(e.target.value)}
                  placeholder="Add notes about how you're feeling (optional)"
                  className="w-full p-3 border rounded-lg text-sm resize-none h-20 dark:bg-gray-800 dark:text-white border-gray-300 dark:border-gray-700 focus:border-purple-500 outline-none"
                />
                
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setShowMoodInput(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleMoodSubmit}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                  >
                    Save Mood
                  </button>
                </div>
              </div>
            )}
            
            {moodHistory.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Moods</h5>
                <div className="flex space-x-2 overflow-x-auto py-2">
                  {moodHistory.slice(0, 5).map((entry, index) => (
                    <div key={index} className="flex flex-col items-center min-w-[60px]">
                      <div className="mb-1">{moodIcons[entry.mood]}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(entry.date).toLocaleDateString(undefined, { weekday: 'short' })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Exercises & Activities */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Menu className="h-4 w-4 text-purple-500 mr-2" />
              Suggested Exercises
            </h4>
            
            <div className="space-y-3">
              {exercises.map((exercise) => (
                <div 
                  key={exercise.id} 
                  className="p-4 border rounded-lg border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white">{exercise.title}</h5>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                      {exercise.duration}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{exercise.description}</p>
                  <div className="flex justify-end">
                    <button className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700">
                      Start Exercise
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
        The Mental Wellness Assistant provides support and resources but is not a replacement for professional mental healthcare.
        If you're experiencing a crisis, please contact emergency services or a mental health professional.
      </p>
    </HealthCard>
  );
};

export default AIMentalHealthBot; 